import { useState } from "react";
import { Mail, Lock, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

function formatDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
}

/**
 * Two-step password reset wizard:
 *   1. Enter email → server emails a 6-digit code (always 200 to prevent
 *      enumeration; UI just moves to step 2 unconditionally).
 *   2. Enter code + new password → server verifies + rotates the password,
 *      issues a session cookie, and we route to /dashboard.
 */
export default function ForgotPasswordModal({ open, onOpenChange, initialEmail = "" }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setStep(1);
    setCode("");
    setNewPw("");
    setLoading(false);
    setError("");
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setStep(2);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        email,
        code: code.trim(),
        new_password: newPw,
      });
      setUser(data);
      toast.success("Password reset — you're signed in.");
      handleOpenChange(false);
      navigate("/dashboard", { replace: true, state: { user: data } });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="forgot-password-modal"
        className="bg-zinc-950 border border-zinc-800 rounded-none max-w-md w-[92vw] p-0 text-white [&>button:last-child]:hidden"
      >
        <div className="p-6 md:p-7 border-b border-zinc-800">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">
            <KeyRound className="w-3 h-3" />
            {step === 1 ? "Reset password · Step 1 of 2" : "Reset password · Step 2 of 2"}
          </div>
          <div className="font-heading text-2xl font-black tracking-tighter leading-tight">
            {step === 1 ? "Where do we send the code?" : "Enter your code."}
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={submitEmail} className="p-6 md:p-7 space-y-4" data-testid="forgot-form-step1">
            <p className="text-sm text-zinc-400 leading-relaxed">
              We'll email a 6-digit code to this address if it belongs to a LinguaSub account. It expires in 15 minutes.
            </p>
            <div className="relative">
              <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type="email"
                data-testid="forgot-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="pl-10 bg-zinc-900 border-zinc-800 rounded-none h-11 text-white placeholder:text-zinc-600"
                required
                autoFocus
              />
            </div>
            {error && (
              <div className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2 font-mono">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="forgot-send-code-btn"
              disabled={loading}
              className="w-full h-11 rounded-none bg-white text-zinc-950 hover:bg-zinc-100 font-medium disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="p-6 md:p-7 space-y-4" data-testid="forgot-form-step2">
            <div className="text-sm text-zinc-400 leading-relaxed">
              We sent a 6-digit code to <span className="text-white font-mono">{email}</span>. Paste it below and pick a new password.
            </div>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="forgot-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                className="pl-10 bg-zinc-900 border-zinc-800 rounded-none h-11 text-white placeholder:text-zinc-600 font-mono text-lg tracking-[0.5em]"
                required
                autoFocus
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="forgot-new-password-input"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="New password (at least 8 characters)"
                minLength={8}
                className="pl-10 bg-zinc-900 border-zinc-800 rounded-none h-11 text-white placeholder:text-zinc-600"
                required
              />
            </div>
            {error && (
              <div className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2 font-mono">
                {error}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setError(""); }}
                data-testid="forgot-back-btn"
                className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1.5"
              >
                <ArrowLeft className="w-3 h-3" /> Change email
              </button>
              <Button
                type="submit"
                data-testid="forgot-reset-btn"
                disabled={loading || code.length !== 6 || newPw.length < 8}
                className="ml-auto h-11 rounded-none bg-white text-zinc-950 hover:bg-zinc-100 font-medium disabled:opacity-50 px-6"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset & sign in"}
              </Button>
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed pt-2 border-t border-zinc-800">
              Didn't get the code? Check spam. Codes expire in 15 minutes — after that, just{" "}
              <button type="button" onClick={() => { setStep(1); setError(""); }} className="text-blue-400 hover:underline">
                request a new one
              </button>
              .
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
